import { useGetReleaseOrVersion } from '@upright/ui/common/upright/model/output/releases/hooks';
import { useQuery } from '@tanstack/react-query';
import { fetchSingleModelEntityData } from '.';
import { ModelEntityReference } from '../../../../../../common/model/input/types';
import { ModelEntityWithScores } from '../../../../../../common/model/output/scores/model-entity-data/types';
import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';
import { FetchSingleModelEntityDataOptions } from '@upright/common/model/output/scores/model-entity-data/api-types';

/**
 * A generic hook that fetches the score data for a model entity.
 * Use entity-specific hooks whenever possible
 * @see [useCompanyScores]({@link ../../../../../views/model-entity-views/company-view/use-company-scores.tsx})
 * @see [useCompanyGroupScores]({@link ../../../../../views/model-entity-views/company-group-view/use-company-group-scores.tsx})
 * @see [usePortfolioScores]({@link ../../../../../views/model-entity-views/portfolio-view/use-portfolio-scores.tsx})
 */
export function useGetModelEntityScoreData<TData = ModelEntityWithScores>({
  modelEntityReference,
  overrides,
  options,
  select,
}: {
  modelEntityReference: ModelEntityReference | undefined;
  overrides?: {
    releaseOrVersion?: string | null;
  };
  options?: FetchSingleModelEntityDataOptions;
  select?: (data: ModelEntityWithScores) => TData;
}) {
  const defaultReleaseOrVersion = useGetReleaseOrVersion()(modelEntityReference?.type);
  const releaseOrVersion = overrides?.releaseOrVersion ?? defaultReleaseOrVersion;

  return useQuery<ModelEntityWithScores, ErrorWithHttpStatus, TData>({
    queryKey: [
      'GetModelEntityScoreData',
      modelEntityReference?.type,
      modelEntityReference?.id,
      releaseOrVersion ?? 'UNSET',
      JSON.stringify(options),
    ],
    queryFn: async () => {
      if (!modelEntityReference) throw Error('Entity reference missing');
      if (!releaseOrVersion) throw Error('Release or version missing');
      return await fetchSingleModelEntityData(modelEntityReference, releaseOrVersion, options);
    },
    enabled: modelEntityReference && !!releaseOrVersion,
    select,
  });
}
